import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import { PUZZEL_OIDC_LOGIN_FLAG } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  CenteredLabelInput,
  DropdownAndInputGroup,
  HorizontalContainer,
  ProfileDetails,
} from "features/Header/PuzzelTelephone/components/Logon/StyledLogonComponents";
import { useCallStatus } from "features/Header/PuzzelTelephone/provider/contexts/CallStatusContext";
import { usePuzzelExtendedStateInformation } from "features/Header/PuzzelTelephone/provider/contexts/ListenForCallStatusContext";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import {
  usePerformUserLogOnOff,
  useUserDetailsState,
} from "features/Header/PuzzelTelephone/provider/contexts/UserDetailsAndLogOnOffContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import {
  removePhoneNumberPrefix,
  validPhoneNumber,
} from "features/Header/PuzzelTelephone/utils";
import { FC, FormEvent, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SkeletonButton } from "shared/components/feedback/SkeletonLoader/SkeletonLoaderStyles";
import {
  Button,
  createListCollection,
  Input,
  Select,
  SelectItem,
  useColorMode,
} from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { QueueHeadsetFill24Icon } from "shared/icons/QueueHeadsetFill24Icon";
import styled, { useTheme } from "styled-components";

type PuzzelProfile = {
  groupProfile: boolean;
  id: string;
  name: string;
  number: number;
  phoneType: string;
  qualities: Array<unknown>;
  phoneNumber?: string;
  lastPhoneNumber?: string;
};

type ProfileSelectItem = {
  value: string;
  label: string;
};

const StyledInput = styled(Input)`
  /* Overrides Spor's default input color (text, a pale mint green), not theme.colorTextMain */
  && {
    color: ${(props) => props.theme.colorTextMain};
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-size: 14px;
    line-height: 21px;
  }
`;

export const Logon: FC = () => {
  const {
    phoneNumber: nonPrefixedPhoneNumber,
    profileId,
    setPhoneNumber,
    setProfileId,
  } = useCallStatus();

  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const theme = useTheme();
  // Vanilla Spor's dark surface default, bypassing DROPS' app-wide teal override (see shared/theme/theme.ts)
  const panelBackground = colorMode === "dark" ? "#001A18" : undefined;
  const { enabled: oidcEnabled } = useFeatureFlag(PUZZEL_OIDC_LOGIN_FLAG);
  const userDetails = useUserDetailsState();
  const { userStateInformation } = usePuzzelExtendedStateInformation();
  const { phoneNumberPrefix, logout } = usePuzzelAuthenticationContext();
  const { userId } = useUserValidationState();
  const performUserLogOnOff = usePerformUserLogOnOff();

  const { currentProfile, profiles, isLoggedOn, isLoggingOnOrOff } =
    userDetails;
  const enableBtn = profileId && validPhoneNumber(nonPrefixedPhoneNumber);

  // userStateInformation is polled every 2s and provides the most up-to-date logon status.
  const isLoggedOnToQueue = userStateInformation
    ? userStateInformation.contactCentreStatus !== "LoggedOff"
    : isLoggedOn;
  const profileName = userStateInformation?.profileName ?? currentProfile?.name;
  const loggedOnPhoneNumber =
    userStateInformation?.phoneNumber ?? currentProfile?.phoneNumber;

  const profileItems = useMemo(
    () =>
      createListCollection<ProfileSelectItem>({
        items:
          profiles?.map((profile: PuzzelProfile) => ({
            label: profile.name,
            value: profile.id,
          })) ?? [],
      }),
    [profiles],
  );

  const logOn = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logon",
        },
        payload: {
          phoneNumber: phoneNumberPrefix + nonPrefixedPhoneNumber,
          profileId,
        },
      });
    },
    [
      performUserLogOnOff,
      userId,
      phoneNumberPrefix,
      nonPrefixedPhoneNumber,
      profileId,
    ],
  );

  const logOff = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logoff",
        },
        payload: {},
      });
    },
    [performUserLogOnOff, userId],
  );

  useEffect(() => {
    if (currentProfile?.id) {
      setProfileId(currentProfile?.id);
    }
  }, [currentProfile?.id]);

  if (isLoggedOn === null) {
    return <SkeletonButton />;
  }

  if (!isLoggedOnToQueue) {
    return (
      <HorizontalContainer onSubmit={logOn}>
        <DropdownAndInputGroup>
          <Select
            collection={profileItems}
            label={t("Telefonkø")}
            width="250px"
            size="sm"
            value={profileId ? [profileId] : []}
            onValueChange={(item) => {
              setProfileId(item.value[0] ?? "");
            }}
            css={{
              '& [data-part="value-text"]': {
                color: theme.colorTextMain,
                fontSize: "14px",
                lineHeight: "21px",
              },
              '& [data-part="content"]': {
                backgroundColor: panelBackground
                  ? `${panelBackground}`
                  : undefined,
              },
            }}
          >
            {profileItems.items.map((item) => (
              <SelectItem
                key={item.value}
                item={item}
                color={theme.colorTextMain}
                fontSize="14px"
                lineHeight="21px"
              >
                {item.label}
              </SelectItem>
            ))}
          </Select>

          <CenteredLabelInput>
            <StyledInput
              type="tel"
              width="250px"
              size="sm"
              onChange={(event) => {
                setPhoneNumber(event.target.value);
              }}
              value={nonPrefixedPhoneNumber}
              label={t("Telefonnummer")}
            />
          </CenteredLabelInput>
        </DropdownAndInputGroup>
        <Button
          size="md"
          variant="primary"
          type="submit"
          disabled={!enableBtn || isLoggingOnOrOff}
          loading={isLoggingOnOrOff}
          loadingText={t("Logger på")}
          leftIcon={<QueueHeadsetFill24Icon />}
          whiteSpace="nowrap"
        >
          {t("Logg på kø")}
        </Button>
        {oidcEnabled && (
          <Button
            size="md"
            variant="tertiary"
            fontWeight="bold"
            type="button"
            onClick={logout}
          >
            {t("Logg ut av Puzzel")}
          </Button>
        )}
      </HorizontalContainer>
    );
  }

  return (
    <HorizontalContainer onSubmit={logOff}>
      <ProfileDetails>
        <Text highlight>{profileName}</Text>
        <Text highlight>
          {removePhoneNumberPrefix(loggedOnPhoneNumber, phoneNumberPrefix)}
        </Text>
      </ProfileDetails>
      <Button
        size="md"
        variant="primary"
        type="submit"
        disabled={isLoggingOnOrOff}
        loading={isLoggingOnOrOff}
        loadingText={t("Logger av")}
        leftIcon={<QueueHeadsetFill24Icon />}
        whiteSpace="nowrap"
      >
        {t("Logg av")}
      </Button>
    </HorizontalContainer>
  );
};
